import React from 'react';
import {WF_FRAME_ID_ATTRIBUTE} from './FrameDomUtils';

/**
 * CSS custom property holding the frame's `vh` reference length (see the
 * style tag below). Written inline on the frame element when `vhBase` is
 * provided, so consumers (e.g. the on-canvas spacing overlay) can read the
 * exact base the rendered CSS resolves `vh` against.
 */
export const FRAME_VH_BASE_CSS_VARIABLE = '--__wf_oc_vh-base';

/**
 * CSS plumbing for frame-relative viewport units on Open Canvas. Declares
 * canonical `--__wf_oc_v*` per-unit lengths (1/100th of axis) and s/d/l
 * aliases on each frame's direct children, so user-authored
 * `Nvh`/`Nsvw`/... can map to unit-specific variables and still resolve to
 * shared frame measurements.
 *
 *  - `vw`/`vmin`/`vmax`: resolve via `1cqi` against the frame's
 *    `container-type: inline-size` ancestor (set on the frame element below).
 *  - `vh`: derived from `--__wf_oc_vh-base`, a breakpoint-height constant
 *    callers write inline via the `vhBase` prop. Anchoring to the breakpoint
 *    matches real-browser `vh` (window height, not document height) and
 *    avoids the feedback loop of tying `vh` to a content-sized frame.
 *  - `vi`/`vb`: mapped to `vw`/`vh` (horizontal-tb). Vertical writing modes
 *    are not swapped yet — `@container style(writing-mode: …)` is spec'd but
 *    unimplemented in browsers (style queries only match custom properties).
 *    A future opt-in could use `--__wf_oc_writing-axis` + `style(--__wf_oc_writing-axis: vertical)`.
 *
 * `@property` registration types each variable as `<length>` so values
 * compute at the declaration site and inherit through any user-authored
 * size containers nested below.
 */
const FRAME_VIEWPORT_UNIT_STYLE_TAG = (
  <style>{`
    [data-wf-frame-id] > * {
      --__wf_oc_vh: calc(var(--__wf_oc_vh-base, 0px) / 100);
      --__wf_oc_svh: var(--__wf_oc_vh);
      --__wf_oc_dvh: var(--__wf_oc_vh);
      --__wf_oc_lvh: var(--__wf_oc_vh);
      --__wf_oc_vw: 1cqi;
      --__wf_oc_svw: var(--__wf_oc_vw);
      --__wf_oc_dvw: var(--__wf_oc_vw);
      --__wf_oc_lvw: var(--__wf_oc_vw);
      --__wf_oc_vmin: min(var(--__wf_oc_vw), var(--__wf_oc_vh));
      --__wf_oc_svmin: var(--__wf_oc_vmin);
      --__wf_oc_dvmin: var(--__wf_oc_vmin);
      --__wf_oc_lvmin: var(--__wf_oc_vmin);
      --__wf_oc_vmax: max(var(--__wf_oc_vw), var(--__wf_oc_vh));
      --__wf_oc_svmax: var(--__wf_oc_vmax);
      --__wf_oc_dvmax: var(--__wf_oc_vmax);
      --__wf_oc_lvmax: var(--__wf_oc_vmax);
      --__wf_oc_vi: var(--__wf_oc_vw);
      --__wf_oc_svi: var(--__wf_oc_vi);
      --__wf_oc_dvi: var(--__wf_oc_vi);
      --__wf_oc_lvi: var(--__wf_oc_vi);
      --__wf_oc_vb: var(--__wf_oc_vh);
      --__wf_oc_svb: var(--__wf_oc_vb);
      --__wf_oc_dvb: var(--__wf_oc_vb);
      --__wf_oc_lvb: var(--__wf_oc_vb);
    }

    @property --__wf_oc_vh-base { syntax: '<length>'; inherits: true; initial-value: 0px }
    @property --__wf_oc_vh      { syntax: '<length>'; inherits: true; initial-value: 0px }
    @property --__wf_oc_svh     { syntax: '<length>'; inherits: true; initial-value: 0px }
    @property --__wf_oc_dvh     { syntax: '<length>'; inherits: true; initial-value: 0px }
    @property --__wf_oc_lvh     { syntax: '<length>'; inherits: true; initial-value: 0px }
    @property --__wf_oc_vw      { syntax: '<length>'; inherits: true; initial-value: 0px }
    @property --__wf_oc_svw     { syntax: '<length>'; inherits: true; initial-value: 0px }
    @property --__wf_oc_dvw     { syntax: '<length>'; inherits: true; initial-value: 0px }
    @property --__wf_oc_lvw     { syntax: '<length>'; inherits: true; initial-value: 0px }
    @property --__wf_oc_vmin    { syntax: '<length>'; inherits: true; initial-value: 0px }
    @property --__wf_oc_svmin   { syntax: '<length>'; inherits: true; initial-value: 0px }
    @property --__wf_oc_dvmin   { syntax: '<length>'; inherits: true; initial-value: 0px }
    @property --__wf_oc_lvmin   { syntax: '<length>'; inherits: true; initial-value: 0px }
    @property --__wf_oc_vmax    { syntax: '<length>'; inherits: true; initial-value: 0px }
    @property --__wf_oc_svmax   { syntax: '<length>'; inherits: true; initial-value: 0px }
    @property --__wf_oc_dvmax   { syntax: '<length>'; inherits: true; initial-value: 0px }
    @property --__wf_oc_lvmax   { syntax: '<length>'; inherits: true; initial-value: 0px }
    @property --__wf_oc_vi      { syntax: '<length>'; inherits: true; initial-value: 0px }
    @property --__wf_oc_svi     { syntax: '<length>'; inherits: true; initial-value: 0px }
    @property --__wf_oc_dvi     { syntax: '<length>'; inherits: true; initial-value: 0px }
    @property --__wf_oc_lvi     { syntax: '<length>'; inherits: true; initial-value: 0px }
    @property --__wf_oc_vb      { syntax: '<length>'; inherits: true; initial-value: 0px }
    @property --__wf_oc_svb     { syntax: '<length>'; inherits: true; initial-value: 0px }
    @property --__wf_oc_dvb     { syntax: '<length>'; inherits: true; initial-value: 0px }
    @property --__wf_oc_lvb     { syntax: '<length>'; inherits: true; initial-value: 0px }
  `}</style>
);

export interface PrimitiveFrameProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'children'
> {
  frameId: string;
  width: number | string;
  height: number | string;
  minWidth?: number;
  maxWidth?: number;
  minHeight?: number;
  backgroundColor?: string;
  /**
   * Reference value (px) for the frame-relative `vh` unit. Pass
   * `getBreakpointHeight(breakpointId)` (verified device height). Omit to leave
   * `--__wf_oc_vh-base` unwritten — `vh` then falls back to the `@property`
   * initial value of `0px`.
   */
  vhBase?: number;
  /**
   * Breakpoint container-name chain (from `getBreakpointContainerName`)
   * declared on the frame element, so breakpoint-scoped
   * `@container wf-container-* (...)` rules resolve against this frame.
   * Omit to keep the frame an unnamed inline-size container (viewport-unit
   * plumbing only).
   */
  breakpointContainerName?: string;
  children: React.ReactNode;
}

/**
 * A pure, data-source-agnostic React component that renders the outer
 * frame element with the standard frame attributes and styles.
 *
 * This is the shared primitive used by both the Component Canvas (via the
 * Frame Atom) and the Open Canvas (via store-driven page frames).
 */
export const PrimitiveFrame = React.forwardRef<
  HTMLDivElement,
  PrimitiveFrameProps
>(function PrimitiveFrame(
  {
    frameId,
    width,
    height,
    minWidth,
    maxWidth,
    minHeight,
    backgroundColor,
    vhBase,
    breakpointContainerName,
    children,
    style,
    ...rest
  },
  ref
) {
  const handleContextMenu = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (event.target === event.currentTarget) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    []
  );

  const frameStyle = {
    transform: 'translateX(0px)',
    overflow: 'hidden',
    container: `${breakpointContainerName ?? 'none'} / inline-size`,
    backgroundColor,
    width,
    height,
    minWidth,
    maxWidth,
    minHeight,
    ...(vhBase != null ? {[FRAME_VH_BASE_CSS_VARIABLE]: `${vhBase}px`} : null),
    ...style,
  } as React.CSSProperties;

  return (
    <>
      {FRAME_VIEWPORT_UNIT_STYLE_TAG}
      <div
        {...rest}
        {...{[WF_FRAME_ID_ATTRIBUTE]: frameId}}
        style={frameStyle}
        ref={ref}
        onContextMenu={handleContextMenu}
      >
        {children}
      </div>
    </>
  );
});
